<template>
  <v-card flat :height="height" class="chronology-list-screen">
    <div class="d-flex align-center my-2">
      <div class="d-flex align-center justify-end flex-grow-1 align-center">
        <ChronologyDateRange @change="onChangeDateRange" ref="dateRange" />
        <v-card :width="250" class="d-flex justify-end pr-1" flat tile>
          <v-switch
            v-model="excludeCompleted"
            class="mr-3 mode-switch"
            label="完了を省く"
            color="primary"
            hide-details
          />
          <v-switch
            v-model="autoLoad"
            class="mode-switch"
            label="自動更新"
            color="primary"
            hide-details
          />
        </v-card>
      </div>
    </div>
    <div class="d-flex align-center mb-2">
      <v-btn @click="showEditorDialog()" class="px-4" small>
        新規登録
      </v-btn>
      <v-btn small @click="refresh()" class="ml-2" text>
        <v-icon small color="#757575" left>mdi-refresh</v-icon>
        <span class="hidden-sm-and-down">最新情報に更新</span>
      </v-btn>
      <v-btn small @click="downloadCsv()" text>
        <v-icon small color="#757575" left>mdi-download</v-icon>
        <span class="hidden-sm-and-down">CSVダウンロード</span>
      </v-btn>
      <v-card class="d-flex flex-wrap justify-end flex-grow-1" flat>
        <v-select
          :items="users"
          style="font-size: 12px; max-width: 120px"
          class="mr-1 mb-1"
          v-model="searchFilter.user"
          dense
          hide-details
          outlined
          @change="filterRow()"
        />
        <v-select
          :items="categories"
          style="font-size: 12px; max-width: 160px"
          class="mr-0 mb-1"
          v-model="searchFilter.category"
          dense
          hide-details
          outlined
          @change="filterRow()"
        />
        <v-text-field
          placeholder="内容・対応状況"
          style="font-size: 12px; max-width: 284px"
          class="ml-1"
          v-model="searchFilter.content"
          dense
          hide-details
          outlined
          clearable
          @input="filterRow()"
        />
      </v-card>
    </div>
    <v-simple-table fixed-header height="calc(100dvh - 196px)">
      <thead>
        <tr>
          <th class="text-center" style="min-width: 46px"></th>
          <th class="text-center" style="min-width: 100px">
            ID
          </th>
          <th class="text-left" style="width: 110px">
            発生/確認日時
          </th>
          <th class="text-left" style="min-width: 160px">
            <div>カテゴリ</div>
            <div>情報種別</div>
          </th>
          <th class="text-left" style="min-width: 100px">
            件名
          </th>
          <th class="text-left" style="min-width: 180px">
            対応状況
          </th>
          <th class="text-left" style="min-width: 110px; width: 110px">
            入力者
          </th>
          <th class="text-center" style="min-width: 53px">
            <div>報告</div>
            <div>履歴</div>
          </th>
          <th class="text-center" style="min-width: 53px">
            <div>状況</div>
            <div>報告</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, i) in rows"
          :key="i"
          @click="showOnMap(row)"
          :style="{
            backgroundColor: row.id === selectedId ? '#FFF8E1' : ''
          }"
          :id="`row-${row.id}`"
        >
          <td class="text-center" :class="getSelectedClass(row)">
            <v-icon :color="row.color">{{ row.icon }}</v-icon>
          </td>
          <td>
            <div style="cursor: pointer" @click.stop="showEditorDialog(row)">
              <div
                class="font-weight-bold blue--text text--darken-4 text-decoration-underline"
                v-if="!row.isJmaAlert"
              >
                {{ row.id }}
              </div>
            </div>
          </td>
          <td class="font-weight-bold grey--text text--darken-2">
            {{ row.jst }}
          </td>
          <td>
            <div>{{ row.category }}</div>
            <div>{{ row.section }}</div>
          </td>
          <td>
            {{ row.notes }}
          </td>
          <td>
            <div class="d-flex" v-if="!row.isJmaAlert">
              <div
                class="font-weight-bold"
                style="width: 70px; min-width: 70px"
                :style="{ color: getStatusColor(row.status) }"
              >
                {{ row.status }}
              </div>
              <template v-if="row.progress">
                <v-card
                  v-if="row.progress.url"
                  width="80"
                  height="56"
                  class="mr-2 text-center"
                  color="transparent"
                  :flat="row.progress.isPdf"
                  @click="previewProgressFile(row.progress.url)"
                >
                  <v-icon v-if="row.progress.isPdf" x-large class="mt-2">
                    mdi-file-pdf-box
                  </v-icon>
                  <v-img
                    v-else
                    width="80"
                    height="56"
                    :src="row.progress.url"
                  />
                </v-card>
                <div v-if="row.progress.text">
                  <div
                    class="font-weight-bold"
                    :style="{ color: getStatusColor(row.status) }"
                  >
                    {{ row.progress.jst }}
                  </div>
                  <div>{{ row.progress.text }}</div>
                </div>
              </template>
            </div>
          </td>
          <td style="width: 160px">
            {{ row.displayUser }}
          </td>
          <td class="text-center">
            <v-btn
              x-small
              @click.stop="showHistory(row)"
              class="mt-2"
              :disabled="!row.progress || row.progress.count === 0"
              v-if="!row.isJmaAlert"
            >
              <v-badge
                color="#FFB74D"
                :content="
                  row.progress && row.progress.count ? row.progress.count : 0
                "
                :value="row.progress && row.progress.count ? true : false"
              >
                履歴
              </v-badge>
            </v-btn>
          </td>
          <td>
            <v-btn
              x-small
              @click.stop="appendProcess(row)"
              class="mt-2"
              v-if="!row.isJmaAlert"
            >
              追加
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <ChronologyEditor
      :dialog="editorDialog"
      :data="targetRow"
      :maxWidth="600"
      @close="closeDialog"
      @closeAndProgress="closeEditorAndProgress"
    />
    <ChronologyDetail
      :dialog="detailDialog"
      :data="detailRow"
      :maxWidth="800"
      @close="closeDialog"
    />
    <ChronologyProgress
      :dialog="progressDialog"
      :data="progressRow"
      :maxWidth="800"
      @close="closeDialog"
    />
    <ChronologyRegister :dialog="registerDialog" @close="closeDialog" />
  </v-card>
</template>
<script>
import axios from "axios";
import moment from "moment";
import { Chronology } from "../../enums/Chronology";
import chronologyAPI from "../../utils/chronologyAPI";
import ChronologyRegister from "./ChronologyRegister";
import ChronologyEditor from "./ChronologyEditor";
import ChronologyDetail from "./ChronologyDetail";
import ChronologyProgress from "./ChronologyProgress";
import ChronologyDateRange from "./ChronologyDateRange";
const ALERT_URL = "https://data.riskma.net/bosai/alert/range";
const ALERT_POINT = "JPNB";
const ALERT_CITIES = ["1738600"];
const ALERT_RIVERS = [];
export default {
  name: "ChronologyListScreen",
  components: {
    ChronologyRegister,
    ChronologyEditor,
    ChronologyDetail,
    ChronologyProgress,
    ChronologyDateRange
  },

  props: {
    map: {
      type: Object
    },
    height: {
      type: [String, Number],
      default: "calc(100dvh - 40px)"
    }
  },

  data() {
    return {
      Chronology,
      baseRows: [],
      rows: [],
      searchFilter: {
        category: "カテゴリ",
        user: "入力者",
        content: ""
      },
      targetRow: null,
      detailRow: null,
      progressRow: null,
      editorDialog: false,
      registerDialog: false,
      detailDialog: false,
      progressDialog: false,
      autoLoad: true,
      excludeCompleted: true,
      subscribeTimer: null,
      dates: [],
      disabledScroll: true
    };
  },

  computed: {
    categories() {
      return ["カテゴリ", ...Chronology.categories];
    },
    selectedId() {
      const row = this.$store.getters.selectedChronology;
      return row ? row.id : 0;
    },
    users() {
      const users = this.baseRows.map(row => row.displayUser);
      users.unshift("入力者");
      if (!users.includes(this.searchFilter.user)) {
        users.push(this.searchFilter.user);
      }
      return Array.from(new Set(users));
    }
  },

  watch: {
    autoLoad(value) {
      if (value) {
        this.fetchChronology();
      } else {
        this.unsubscribe();
      }
    },
    excludeCompleted(newval, oldval) {
      if (newval !== oldval) {
        this.filterRow();
      }
    },
    selectedId(val) {
      if (this.disabledScroll) return;
      // リストを見える位置にスクロール
      const targetIndex = this.rows.findIndex(row => row.id === val);
      if (targetIndex < 0) return;
      const id = `#row-${this.rows[targetIndex].id}`;
      const height = this.$el.querySelector(id).clientHeight;
      const tableWrapper = this.$el.getElementsByClassName(
        "v-data-table__wrapper"
      )[0];
      tableWrapper.scroll({ top: height * targetIndex, behavior: "smooth" });
    }
  },

  created() {
    this.searchFilter.category = this.categories[0];
    document.addEventListener("visibilitychange", this.visibilityChange);
  },

  beforeDestroy() {
    this.unsubscribe();
    document.removeEventListener("visibilitychange", this.visibilityChange);
  },

  mounted() {
    console.log("mounted");
    this.fetchChronology();
  },

  activated() {
    console.log("activated");
  },

  methods: {
    visibilityChange() {
      if (document.visibilityState === "visible") {
        this.fetchChronology();
      }
    },

    showEditorDialog(row = null) {
      this.targetRow = row;
      if (row === null) {
        this.registerDialog = true;
      } else {
        this.editorDialog = true;
      }
    },

    getJst(date) {
      return moment
        .utc(date, "YYYYMMDDHHmm")
        .local()
        .format("YYYY/MM/DD HH:mm");
    },

    showS3Url(url) {
      window.open(url);
    },

    sortBaseRows() {
      this.baseRows.forEach(row => {
        if (row.progress && row.progress.date) {
          row.progress.jst = moment
            .utc(row.progress.date, "YYYYMMDDHHmm")
            .local()
            .format("YYYY/MM/DD HH:mm");
        }
        row.jst = moment
          .utc(row.date, "YYYYMMDDHHmm")
          .local()
          .format("YYYY/MM/DD HH:mm");
        if (row.progress && row.progress.s3Key) {
          row.progress.url = `https://hodatsushimizu-chronology.riskma.jp/${row.progress.s3Key}`;
          row.progress.isPdf = row.progress.s3Key.endsWith(".pdf");
        }
        row.displayUser =
          row.progress && row.progress.user ? row.progress.user : row.user;
      });
      return this.baseRows.sort((a, b) => {
        if (a.jst < b.jst) return 1;
        if (a.jst > b.jst) return -1;
        return 0;
      });
    },

    unsubscribe() {
      clearInterval(this.subscribeTimer);
    },

    async subscribe() {
      this.unsubscribe();
      this.subscribeTimer = setInterval(() => {
        this.fetchChronology();
      }, 60 * 1000);
    },

    getBaseDate() {
      if (this.dates.length === 0) {
        return moment.utc();
      } else {
        const d = this.dates.length === 1 ? this.dates[0] : this.dates[1];
        return moment.utc(d + "14:59", "YYYY-MM-DD HH:mm");
      }
    },

    getStartDate() {
      if (this.dates.length === 0) {
        return null;
      } else {
        const d = this.dates[0];
        return moment.utc(d + "15:00", "YYYY-MM-DD HH:mm").subtract(1, "day");
      }
    },

    async fetchChronology() {
      const params = {
        date: this.getBaseDate().format("YYYYMMDDHHmm"),
        category: "",
        excludeCompleted: false
      };
      if (this.getStartDate() !== null) {
        params.startDate = this.getStartDate().format("YYYYMMDDHHmm");
      }
      const response = await chronologyAPI.get("/chronology", params);
      const chronologies =
        response && response.messages ? response.messages : [];
      const alerts = await this.fetchJmaAlert();
      const messages = [...chronologies, ...alerts];

      if (messages.length > 0) {
        this.baseRows = messages;
        this.sortBaseRows();
        this.filterRow();
      } else {
        this.baseRows = [];
        this.rows = [];
      }
      if (this.autoLoad) {
        this.subscribe();
      }
    },

    async fetchJmaAlert() {
      const params = {
        point: ALERT_POINT,
        city: ALERT_CITIES.join(","),
        river: ALERT_RIVERS.join(","),
        date1: "",
        date2: this.getBaseDate().format("YYYY/MM/DD HH:mm")
      };
      if (this.getStartDate() !== null) {
        params.date1 = this.getStartDate().format("YYYY/MM/DD HH:mm");
      } else {
        params.date1 = moment
          .utc()
          .subtract(120, "day")
          .format("YYYY/MM/DD HH:mm");
      }
      const response = await axios
        .create({
          responseType: "json"
        })
        .get(ALERT_URL, { params });
      if (response === null) {
        return [];
      }
      return response.data.map(item => {
        item.isJmaAlert = true;
        item.id =
          item.category +
          item.date
            .replaceAll(" ", "")
            .replaceAll("/", "")
            .replaceAll(":", "");
        item.date = moment
          .utc(item.date, "YYYY/MM/DD HH:mm")
          .format("YYYYMMDDHHmm");
        item.icon = "mdi-information";
        item.color = "#2196F3";
        item.status = "";
        item.category = item.heading;
        item.section = item.title;
        item.notes = item.text;
        item.progress = {
          date: "",
          text: item.comment,
          user: "気象庁防災情報"
        };
        return item;
      });
    },

    filterRow() {
      let rows = this.baseRows;

      if (this.excludeCompleted === true) {
        rows = rows.filter(row => {
          return row.status !== "完了";
        });
      }

      if (this.searchFilter.category !== this.categories[0]) {
        rows = rows.filter(row => {
          return row.category === this.searchFilter.category;
        });
      }

      if (this.searchFilter.user !== this.users[0]) {
        rows = rows.filter(row => {
          return row.displayUser === this.searchFilter.user;
        });
      }

      if (this.searchFilter.content) {
        rows = rows.filter(row => {
          return (
            row.notes.includes(this.searchFilter.content) ||
            (row.progress &&
              row.progress.text.includes(this.searchFilter.content))
          );
        });
      }

      this.rows = rows;
      this.$store.commit("SET_CHRONOLOGIES", [...this.rows]);
    },

    showOnMap(row) {
      if (row.isJmaAlert) return;
      this.$store.commit("SET_SELECTED_CHRONOLOGY", row);
      if (!this.map) return;
      if (row.lat && row.lng) {
        this.map.setView([row.lat, row.lng], 16);
      }
    },

    showHistory(row) {
      if (row.isJmaAlert) return;
      this.detailRow = row;
      this.detailDialog = true;
    },

    appendProcess(row, user = "") {
      if (row.isJmaAlert) return;
      this.progressRow = {
        hash: `${row.hash}-${row.id}`,
        id: 0,
        chronologyId: row.id,
        user: user
      };
      this.progressDialog = true;
    },

    getSelectedClass(row) {
      return row.id === this.selectedId ? "selected" : "unselected";
    },

    previewProgressFile(url) {
      window.open(url, "_blank");
    },

    getStatusColor(status) {
      if (status === "完了") {
        return "#4CAF50";
      } else if (status === "対応中") {
        return "#2196F3";
      } else if (status === "未対応") {
        return "#E53935";
      } else {
        return "grey";
      }
    },

    refresh() {
      if (this.dates.length > 0) {
        this.$refs.dateRange.reset();
      } else {
        this.fetchChronology();
      }
    },

    closeDialog() {
      this.registerDialog = false;
      this.editorDialog = false;
      this.detailDialog = false;
      this.progressDialog = false;
      this.fetchChronology();
    },

    closeEditorAndProgress(row) {
      this.editorDialog = false;
      this.appendProcess(row, row.user);
    },

    downloadCsv() {
      const getCsvItem = val => {
        return val
          .replaceAll("\r\n", "")
          .replaceAll("\n", "")
          .replaceAll(",", "、");
      };
      const filename =
        "chronology-" +
        moment
          .utc()
          .local()
          .format("YYYYMMDDHHmmss") +
        ".csv";
      let data =
        "ID,発生/確認日時,カテゴリ,情報種別,件名,対応状況,対応状況,対応日時,入力者,緯度,経度\r\n";
      this.rows.forEach(row => {
        if (row.isJmaAlert) return;
        const progress = getCsvItem(row.progress ? row.progress.text : "");
        const notes = getCsvItem(row.notes);
        const progressJst = row.progress ? row.progress.jst : ""
        data += `${row.id},${row.jst},${row.category},${row.section},${notes},${row.status},${progress},${progressJst},${row.displayUser},${row.lat},${row.lng}\r\n`;
      });

      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      var elm = document.createElement("a");
      elm.download = filename;
      elm.href = URL.createObjectURL(
        new Blob([bom, data], { type: "text/csv" })
      );
      elm.dataset.downloadurl = ["text/csv", elm.download, elm.href].join(":");
      elm.click();
    },

    onChangeDateRange(dates) {
      const arr = !dates ? [] : [...dates];
      arr.sort();
      this.dates = arr;
      if (this.dates.length > 0) {
        this.autoLoad = false;
      }
      this.fetchChronology();
    }
  }
};
</script>

<style lang="scss" scoped>
th {
  padding: 3px 6px !important;
  font-size: 14px !important;
}
td {
  padding: 10px 6px !important;
  font-size: 14px !important;
}
th {
  background-color: #6f6f6f !important;
  color: #fcfcfc !important;
}
td {
  &:not(:nth-child(1)) {
    vertical-align: top !important;
  }
}
tr {
  &:nth-child(2n) {
    background-color: #fcfcfc;
  }
}
.selected {
  border-left: 4px solid rgb(255, 121, 121);
}
.unselected {
  border-left: 4px solid rgb(220, 220, 220);
}
.long-text {
  font-family: monospace;
  max-height: 50px;
  overflow: hidden;
  color: #6f6f6f;
}
</style>

<style lang="scss">
.mode-switch .v-label {
  font-size: 12px;
  color: #252525;
}
.chronology-list-screen {
  .v-input__slot {
    min-height: 30px !important;
  }
  .v-select__selections {
    padding: 0 !important;
  }
  .v-input__append-inner {
    margin-top: 4px !important;
  }
  .v-input--selection-controls {
    margin-top: 0 !important;
  }
}
</style>
